import React, { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { useScrollToTop } from "../../hooks/useScrollToTop";
import PerformanceMonitor from "../molecules/PerformanceMonitor";
import { Sidebar } from "./Sidebar";
import { TopNavigation } from "./TopNavigation";

interface DashboardLayoutProps {
  children?: React.ReactNode;
}

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  children,
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Scroll to top when route changes
  useScrollToTop("auto");

  // Handle responsive behavior
  useEffect(() => {
    const checkScreenSize = () => {
      const mobile = window.innerWidth < 1024; // lg breakpoint
      setIsMobile(mobile);
      if (!mobile) {
        setSidebarOpen(false); // Close sidebar on desktop
      }
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const closeSidebar = () => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  return (
    <div className="flex h-screen bg-neutral-50">
      {/* Mobile Overlay */}
      {sidebarOpen && isMobile && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <Sidebar onClose={closeSidebar} isMobile={isMobile} />
      </div>

      {/* Main Content Area */}
      <div className="flex overflow-hidden flex-col flex-1 lg:ml-0">
        {/* Header */}
        <TopNavigation onMenuClick={() => setSidebarOpen(!sidebarOpen)} />

        {/* Main Content */}
        <main className="overflow-y-auto flex-1">{children || <Outlet />}</main>
      </div>

      {/* Performance Monitor - Only show in development or when explicitly enabled */}
      {import.meta.env.MODE === "development" && <PerformanceMonitor />}
    </div>
  );
};
